import { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { RootState } from "@/app/rootReducer";
import { fetchLibraryModulesAction } from "@/modules/learningCenter/features/ModuleLibrary/slices/libraryModuleListSlice";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { OnChangeFn, SortingState } from "@/components/Datatable/types";

export interface LibraryModuleListFilters {
  search: string;
  status: string;
  difficulty: string;
  tag: string;
}

export const useLibraryModuleList = () => {
  const dispatch = useAppDispatch();

  const modules = useAppSelector(
    (state: RootState) => state.libraryModuleList.modules,
  );
  const totalCount = useAppSelector(
    (state: RootState) => state.libraryModuleList.totalCount,
  );
  const loading = useAppSelector(
    (state: RootState) => state.libraryModuleList.fetchLoading,
  );
  const error = useAppSelector(
    (state: RootState) => state.libraryModuleList.fetchError,
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([
    { id: "title", desc: false },
  ]);
  const [filters, setFilters] = useState<LibraryModuleListFilters>({
    search: "",
    status: "",
    difficulty: "",
    tag: "",
  });

  const buildParams = useCallback(() => {
    const sortBy = sorting.length ? sorting[0].id : undefined;
    const sortOrder = sorting.length
      ? sorting[0].desc
        ? "desc"
        : "asc"
      : undefined;

    return {
      search: filters.search || undefined,
      status: filters.status || undefined,
      difficulty: filters.difficulty || undefined,
      tag: filters.tag || undefined,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      sortBy,
      sortOrder,
    } as const;
  }, [filters, pagination, sorting]);

  useEffect(() => {
    dispatch(fetchLibraryModulesAction(buildParams()));
  }, [dispatch, buildParams]);

  const refetch = () => {
    dispatch(fetchLibraryModulesAction(buildParams()));
  };

  const handlePaginationChange: OnChangeFn<PaginationState> = (
    updaterOrValue,
  ) => {
    setPagination((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    setSorting((old) =>
      typeof updaterOrValue === "function"
        ? updaterOrValue(old)
        : updaterOrValue,
    );
  };

  const handleFilterChange = (
    filterKey: keyof LibraryModuleListFilters,
    value: string,
  ) => {
    setFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return {
    modules,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    filters,
    refetch,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
  };
};
